import { useEffect, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import './Navigation.css';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { selectAllRoutes } from 'components/GlobalState/selectors/selectAllRoutes';
import { selectApiScopeStringSet } from 'components/GlobalState/selectors/selectApiScopeStringSet';
import { selectMenuTree } from 'components/GlobalState/selectors/selectMenuTree';
import type { MenuTreeNode } from 'components/Sidebar/menuTree';
import deepEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';
import { NavigationLink } from './NavigationLink';
import { NavigationPinnedPages } from './NavigationPinnedPages';
import { NavigationSubmenu } from './NavigationSubmenu';
import { getUrl } from './helpers/getUrl';
import { navigationIncludesCurrentPath } from './utils/navigationIncludesCurrentPath';

const getLabel = (menuTreeNode: MenuTreeNode) => (
  <div className='navigation__label flex items-center'>
    {menuTreeNode.iconClassName && (
      <i
        className={classNames(
          'fa-fw fa-regular',
          menuTreeNode.iconClassName,
          'mr-1.5',
        )}
      />
    )}
    <div
      className='navigation__label-text'
      data-testid={`navigation-label-${menuTreeNode.label}`}
    >
      {menuTreeNode.label}
      {menuTreeNode.preview && (
        <Badge className='ml-2' color='info'>
          Beta
        </Badge>
      )}
    </div>
  </div>
);

interface Props {
  compact: boolean;
}

export const Navigation: FC<Props> = (props) => {
  const globalStateCtxProps = useGlobalStateContextSelectors({
    allRoutes: selectAllRoutes,
    minimisedMenu: prop('minimisedMenu'),
    apiScopeStringSet: selectApiScopeStringSet,
    scope: prop('auth', 'scope'),
    admin: prop('auth', 'admin'),
    menuTree: selectMenuTree,
    theme: prop('user', 'theme'),
  });

  const location = useLocation();

  const [visiblePath, setVisiblePath] = useState<number[]>();

  const visibilityTimeoutRef = useRef<number | null>(null);
  const DELAY = 800;
  const setVisible = (visibilityArg?: number[]) => {
    if (visibilityTimeoutRef.current) {
      window.clearTimeout(visibilityTimeoutRef.current);
    }

    if (visibilityArg === undefined) {
      visibilityTimeoutRef.current = window.setTimeout(() => {
        setVisiblePath(undefined);
      }, DELAY);
    }

    if (visibilityArg) {
      setVisiblePath(visibilityArg);
    }
  };

  const [selectedMenu, setSelectedMenu] = useState<number[]>([]);
  const getSelectedMenu = useStableCallback((): number[] => {
    const reduceSelected = (
      acc: number[],
      menuTreeNode: MenuTreeNode,
      index: number,
    ) => {
      const includes = navigationIncludesCurrentPath(menuTreeNode, location);

      if (includes) {
        acc.push(index);

        if (Array.isArray(menuTreeNode.children)) {
          return menuTreeNode.children.reduce<number[]>(reduceSelected, acc);
        }
      }

      return acc;
    };

    return globalStateCtxProps.menuTree.reduce<number[]>(reduceSelected, []);
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies(location.pathname): Updating only on pathname change internally
  useEffect(() => {
    setSelectedMenu(getSelectedMenu());
  }, [location.pathname]);

  const toggleMenu = (path: number[]) => {
    if (deepEqual(path, selectedMenu.slice(0, path.length))) {
      setSelectedMenu(selectedMenu.slice(0, Math.max(0, path.length - 1)));
    } else {
      setSelectedMenu(path);
    }
  };

  function renderSubmenu(
    menuTreeNode: MenuTreeNode,
    pathOpen: number[],
    path: number[],
    index: number,
  ) {
    const [openIndex, ...restPathOpen] = pathOpen;
    const currentPath = [...path, index];

    const submenu = (
      <>
        {menuTreeNode._key === 'technology' && (
          <NavigationPinnedPages compact={props.compact} />
        )}
        {renderTreeMenu(
          menuTreeNode.children!,
          currentPath,
          openIndex === index ? restPathOpen : [],
        )}
      </>
    );

    return (
      <NavigationSubmenu
        key={index}
        index={index}
        item={menuTreeNode}
        path={path}
        pathOpen={pathOpen}
        compact={props.compact}
        submenu={submenu}
        toggleMenu={toggleMenu}
        label={getLabel(menuTreeNode)}
        visiblePath={visiblePath}
        setVisiblePath={setVisible}
      />
    );
  }

  function renderLink(
    menuTreeNode: MenuTreeNode,
    index: number,
    path: number[],
  ) {
    const url = getUrl(menuTreeNode);
    if (url === undefined) {
      stumble(`Menu item ${menuTreeNode.label} has no URL defined`);
      return null;
    }

    return (
      <NavigationLink
        key={index}
        index={index}
        item={menuTreeNode}
        url={url ?? '/'}
        path={path}
        compact={props.compact}
        label={getLabel(menuTreeNode)}
      />
    );
  }

  const renderTreeMenu = (
    menuTreeNodes: MenuTreeNode[],
    path: number[],
    pathOpen: number[],
  ): ReactNode[] =>
    menuTreeNodes.map((item, index) => {
      if (Array.isArray(item.children)) {
        return renderSubmenu(item, pathOpen, path, index);
      }

      return renderLink(item, index, path);
    });

  return (
    <nav
      className={classNames(
        'navigation',
        globalStateCtxProps.theme === 'dark' ? 'theme--dark' : 'theme--light',
        globalStateCtxProps.minimisedMenu && 'navigation--collapsed',
      )}
    >
      <ul>{renderTreeMenu(globalStateCtxProps.menuTree, [], selectedMenu)}</ul>
    </nav>
  );
};
